import { useCallback, useState } from "react";

import type { HeatmapCellValue } from "./heatmapScale";
import type { HeatmapCell } from "./types";

import { applyValueToHeatmapCells, getHeatmapRectangleCells, isHeatmapCellInRectangle } from "./heatmapRange";

export const useHeatmapRangeSelection = (
  values: HeatmapCellValue[][],
  onValuesChange: (values: HeatmapCellValue[][]) => void
) => {
  const [anchorCell, setAnchorCell] = useState<HeatmapCell | undefined>();
  const [hoverCell, setHoverCell] = useState<HeatmapCell | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingCells, setPendingCells] = useState<HeatmapCell[]>([]);

  const resetSelection = useCallback(() => {
    setAnchorCell(undefined);
    setHoverCell(undefined);
    setPendingCells([]);
    setModalOpen(false);
  }, []);

  const handleCellClick = useCallback(
    (cell: HeatmapCell) => {
      if (!anchorCell) {
        setAnchorCell(cell);
        setHoverCell(cell);
        return;
      }

      setPendingCells(getHeatmapRectangleCells(anchorCell, cell));
      setModalOpen(true);
    },
    [anchorCell]
  );

  const handleCellEnter = useCallback(
    (cell: HeatmapCell) => {
      if (modalOpen) return;
      setHoverCell(cell);
    },
    [modalOpen]
  );

  const handleCellLeave = useCallback(() => {
    if (modalOpen) return;
    setHoverCell(anchorCell);
  }, [anchorCell, modalOpen]);

  const isCellHovered = useCallback(
    (cell: HeatmapCell): boolean => {
      if (modalOpen || !hoverCell) return false;
      return hoverCell.columnIndex === cell.columnIndex && hoverCell.rowIndex === cell.rowIndex;
    },
    [hoverCell, modalOpen]
  );

  const isCellHighlighted = useCallback(
    (cell: HeatmapCell): boolean => {
      if (modalOpen) {
        return pendingCells.some(
          (pending) => pending.columnIndex === cell.columnIndex && pending.rowIndex === cell.rowIndex
        );
      }

      if (!anchorCell || !hoverCell) return false;
      return isHeatmapCellInRectangle(cell, anchorCell, hoverCell);
    },
    [anchorCell, hoverCell, modalOpen, pendingCells]
  );

  const applyValue = useCallback(
    (value: number) => {
      onValuesChange(applyValueToHeatmapCells(values, pendingCells, value));
      resetSelection();
    },
    [onValuesChange, pendingCells, resetSelection, values]
  );

  return {
    applyValue,
    handleCellClick,
    handleCellEnter,
    handleCellLeave,
    isCellHighlighted,
    isCellHovered,
    modalOpen,
    pendingCellCount: pendingCells.length,
    resetSelection,
  };
};
